import axios from "axios";
import { generateAiContentURL, generateAiFeedbackURL } from "./URL";

/**
 * Calls the secure backend proxy endpoint for Gemini generateContent.
 * Prevents exposing the Google Gemini API key on the frontend.
 *
 * @param {Object} options
 * @param {Array} [options.contents] - Gemini contents array (same schema as Gemini REST API)
 * @param {string} [options.prompt] - Optional text prompt
 * @param {string} [options.model] - Optional Gemini model
 * @param {string} [options.token] - Optional Bearer token (session cookie is used by default)
 * @returns {Promise<Object>} API response with status and data ({ text, candidates, raw })
 */
export async function generateContentWithGemini({
  contents,
  prompt,
  model,
  token,
}) {
  const payload = contents ? { contents, model } : { prompt, model };
  const headers = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await axios.post(generateAiContentURL, payload, {
    headers,
    withCredentials: true,
  });
  return response.data;
}

/**
 * Calls backend endpoint to generate constructive feedback note for a student.
 * Supports passing structured studentName, courseName, and ratings,
 * or raw prompt/contents.
 *
 * @param {Object} options
 * @param {string} [options.studentName]
 * @param {string} [options.courseName]
 * @param {Array|Object} [options.ratings] - e.g. [{ attribute, rating: 1-5 }]
 * @param {string} [options.prompt]
 * @param {Array} [options.contents]
 * @param {string} [options.token] - Optional Bearer token (session cookie is used by default)
 * @returns {Promise<Object>} API response with status and data ({ text, candidates, raw })
 */
export async function generateStudentFeedbackWithAi({
  studentName,
  courseName,
  ratings,
  prompt,
  contents,
  token,
}) {
  let payload;
  if (contents) {
    payload = { contents };
  } else if (prompt) {
    payload = { prompt };
  } else {
    payload = {
      studentName,
      courseName,
      ratings,
    };
  }

  const headers = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await axios.post(generateAiFeedbackURL, payload, {
    headers,
    withCredentials: true,
  });
  return response.data;
}
